import { Currency } from "@redotech/money/currencies";
import { getCurrencyFormatter } from "@redotech/money/util";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod/v4";
import { SlideInPanelAnchorLocation } from "../slide-in-panel/slide-in-panel-anchor-location";

const discountCombinesWithSchema_ = z.object({
  orderDiscounts: z.boolean().optional(),
  productDiscounts: z.boolean().optional(),
  shippingDiscounts: z.boolean().optional(),
});
export const discountCombinesWithSchema: z.ZodType<DiscountCombinesWith> =
  discountCombinesWithSchema_;
export interface DiscountCombinesWith
  extends z.infer<typeof discountCombinesWithSchema_> {}

const baseDiscountSchema = z.object({
  autoGenerateRandomCode: z.boolean().optional(),
  title: z.string().optional(),
  combinesWith: discountCombinesWithSchema.optional(),
});

const fixedAmountDiscountSchema = baseDiscountSchema.extend({
  discountType: z.literal("fixed_amount"),
  amount: z.object({
    currencyCode: createEnum(Currency, { enumName: "Currency" }),
    value: z.string(),
  }),
});

const percentageDiscountSchema = baseDiscountSchema.extend({
  discountType: z.literal("percentage"),
  percentage: z.string(),
});

export const rescueModalDiscountSchema = z.union([
  fixedAmountDiscountSchema,
  percentageDiscountSchema,
]);
export type RescueModalDiscount = z.infer<typeof rescueModalDiscountSchema>;

export const RESCUE_MODAL_UI_BUTTON_SUBTEXT_DEFAULT = {
  shouldShow: "if_coverage_eligible" as const,
  value: "(Checkout+ included)",
};
export function getDefaultRescueModalTitle(
  discount: RescueModalDiscount | undefined,
): string {
  function formatDiscount(discount: RescueModalDiscount): string {
    if (discount.discountType === "percentage") {
      return `${discount.percentage}%`;
    }
    const formatter = getCurrencyFormatter(discount.amount.currencyCode);
    return formatter.format(parseFloat(discount.amount.value));
  }

  if (!discount) {
    return "Your discount is waiting";
  }
  return `Your ${formatDiscount(discount)} discount is waiting`;
}
export const DEFAULT_RESCUE_MODAL_DESCRIPTION =
  "Go to checkout with the button below to complete your purchase and save on this order.";
export const DEFAULT_RESCUE_MODAL_BUTTON_TEXT = "Apply discount";
export const DEFAULT_RESCUE_MODAL_PLACEMENT =
  SlideInPanelAnchorLocation.TOP_LEFT;

export const rescueModalUiButtonSubtextSchema = z.union([
  z.object({
    shouldShow: z.union([
      z.literal("always"),
      z.literal("if_coverage_eligible"),
    ]),
    value: z.string().optional(),
  }),
  z.object({ shouldShow: z.literal("never") }),
]);
export type RescueModalUiButtonSubtext = z.infer<
  typeof rescueModalUiButtonSubtextSchema
>;

export const rescueModalUiSchema = z.object({
  button: z
    .object({
      text: z.string().optional(),
      subtext: rescueModalUiButtonSubtextSchema.optional(),
    })
    .optional(),
  title: z.string().optional(),
  description: z.string().optional(),
  placement: createEnum(SlideInPanelAnchorLocation, {
    enumName: "SlideInPanelAnchorLocation",
  }).optional(),
});
export type RescueModalUi = z.infer<typeof rescueModalUiSchema>;

export const DEFAULT_DROPOFF_PERCENTAGE_TRIGGER = 50;

export const rescueModalSchema = z.object({
  enabled: z.boolean(),
  discountCode: z.string().optional(),
  discount: rescueModalDiscountSchema.optional(),
  dropoffPercentageTrigger: z.number().min(0).max(100).optional(),
  secondsInColdTrigger: z.number().optional(),
  ui: rescueModalUiSchema.optional(),
});
export type RescueModal = z.infer<typeof rescueModalSchema>;

export const rescueModalDefault: RescueModal = { enabled: false };

/** Prefix for Multi-Model Endpoint names (e.g., checkout-hazard-mme-001) */
export const MME_ENDPOINT_NAME_PREFIX = "checkout-hazard-mme";

/** Default MME endpoint name for checkout hazard model */
export const DEFAULT_MME_ENDPOINT_NAME =
  `${MME_ENDPOINT_NAME_PREFIX}-001` as const;

export const activeCheckoutHazardModelSchema = z.object({
  endpointName: z.string(),
  modelVersion: z.enum(["v-08-11-25", "v-11-25-25"]).default("v-08-11-25"),
});
export type ActiveCheckoutHazardModel = z.infer<
  typeof activeCheckoutHazardModelSchema
>;

export const checkoutHazardBuildStatusEnum = z.enum([
  "succeeded",
  "failed",
  "insufficient_data",
  "running",
]);
export type CheckoutHazardBuildStatus = z.infer<
  typeof checkoutHazardBuildStatusEnum
>;

/**
 * Snowflake-observed volume/recency metrics used by the data sufficiency gate.
 * Persisted on `insufficient_data` build results so the admin UI can render
 * the specific blocker (e.g. "610/10,000 sessions") instead of an opaque
 * "queued for training" forever.
 */
const hazardDataMetricsSchema_ = z.object({
  totalSessions: z.number(),
  convertedSessions: z.number(),
  dataSpanDays: z.number(),
});
export const hazardDataMetricsSchema: z.ZodType<HazardDataMetrics> =
  hazardDataMetricsSchema_;
export interface HazardDataMetrics
  extends z.infer<typeof hazardDataMetricsSchema_> {}

/**
 * Minimum data volume required for the hazard model to train. Enforced by
 * `checkDataSufficiencyActivity` against the Snowflake `hazard_buckets_15s`
 * table (60-day lookback). Values below any one of these thresholds produce
 * an `insufficient_data` build result.
 *
 * Centralized here so the activity, the admin UI, and the Zod schema all
 * reference the same source of truth.
 */
export const HAZARD_DATA_SUFFICIENCY_THRESHOLDS: HazardDataMetrics = {
  totalSessions: 10_000,
  convertedSessions: 500,
  dataSpanDays: 14,
};

const checkoutHazardBuildResultSchema_ = z.object({
  status: checkoutHazardBuildStatusEnum,
  completedAt: z.date(),
  failureCount: z.number().optional(),
  /**
   * Present only on `status === "insufficient_data"` results. Captures the
   * actual Snowflake metrics observed at the time the sufficiency gate
   * rejected the team, so the admin UI can show the delta against
   * {@link HAZARD_DATA_SUFFICIENCY_THRESHOLDS}.
   */
  metrics: hazardDataMetricsSchema.optional(),
  /**
   * Present only on `status === "running"` results. The ARN of the AWS Step
   * Function execution so the next workflow run can check whether it completed
   * (succeeded / failed) while the previous Temporal workflow was timed out.
   */
  executionArn: z.string().optional(),
});
export const checkoutHazardBuildResultSchema: z.ZodType<CheckoutHazardBuildResult> =
  checkoutHazardBuildResultSchema_;
export interface CheckoutHazardBuildResult
  extends z.infer<typeof checkoutHazardBuildResultSchema_> {}

export const predictiveConversionSettingsSchema = z.object({
  enabled: z.boolean(),
  dataCollectionStartedAt: z.date().optional(),
  intentRequested: z.boolean().optional(),
  activeCheckoutHazardModel: activeCheckoutHazardModelSchema.optional(),
  checkoutHazardBuildResult: checkoutHazardBuildResultSchema.optional(),
  rescueModal: rescueModalSchema.optional(),
  intentActivatedAt: z.date().optional(),
});
export type PredictiveConversionSettings = z.infer<
  typeof predictiveConversionSettingsSchema
>;

export const predictiveConversionSettingsDefault: PredictiveConversionSettings =
  { enabled: false };

/** Minimum days of checkout data required before the hazard model is trainable. */
export const MIN_DATA_COLLECTION_DAYS = 14;

/**
 * Number of consecutive retrain failures after which the UI surfaces a hard
 * "Training failed" state instead of "Retry scheduled". The build pipeline
 * itself keeps retrying with Fibonacci backoff regardless — this is purely a
 * presentational threshold.
 */
export const MAX_INTENT_FAILURE_COUNT_BEFORE_FAILED = 5;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Fibonacci-day backoff for checkout hazard model build retries. Used by the
 * cron task to gate retry eligibility and by the admin UI to forecast the next
 * attempt date.
 */
export function fibonacciDays(n: number): number {
  if (n <= 1) {
    return 0;
  }
  let a = 0;
  let b = 1;
  for (let i = 0; i < n; i++) {
    [a, b] = [b, a + b];
  }
  return a;
}

/**
 * Returns the next run time for the daily checkout-hazard build readiness cron
 * (`0 3 * * *`). If `now` is before 03:00 UTC today, returns today at 03:00
 * UTC; otherwise tomorrow at 03:00 UTC.
 */
export function nextBuildReadinessCronRun(now: Date): Date {
  const next = new Date(now);
  next.setUTCHours(3, 0, 0, 0);
  if (next.getTime() <= now.getTime()) {
    next.setUTCDate(next.getUTCDate() + 1);
  }
  return next;
}

/**
 * Derived admin-UI status for a team's predictive conversion / intent pipeline.
 * The primary dimension is whether `activeCheckoutHazardModel.endpointName` is
 * set — that's the source of truth for "are we actually serving predictions."
 * Everything else is secondary signal about freshness / retrain health.
 */
export const intentStatusSchema = z.discriminatedUnion("status", [
  // Endpoint set → team is serving predictions.
  z.object({
    status: z.literal("live"),
    /**
     * When the currently-serving model was trained — sourced from the most
     * recent successful build's `completedAt`. Optional because we don't have
     * that timestamp for pre-existing teams activated before the build-result
     * tracking was added.
     */
    modelServingSince: z.date().optional(),
  }),
  z.object({
    status: z.literal("live_retrain_failing"),
    failureCount: z.number(),
    lastFailureAt: z.date(),
  }),
  // Endpoint not set → team is not serving predictions.
  z.object({
    status: z.literal("not_requested"),
    /**
     * The earliest date a team could reach `live` if they flipped the toggle
     * right now — `max(dataCollectionStartedAt ?? now, now) + 14 days`, give or
     * take one cron tick.
     */
    earliestLiveAt: z.date(),
  }),
  z.object({
    status: z.literal("collecting_data"),
    eligibleToTrainAt: z.date(),
  }),
  z.object({
    status: z.literal("queued_for_training"),
    nextCronRunAt: z.date(),
  }),
  /**
   * A build workflow is currently running — the Step Function has been started
   * and the workflow is either actively polling or timed out (with the next
   * daily cron run set to recover the result).
   */
  z.object({
    status: z.literal("training"),
    startedAt: z.date(),
    nextCronRunAt: z.date(),
  }),
  /**
   * The team is eligible to train (data collection window satisfied, intent
   * requested, no active endpoint), the workflow ran, and the Snowflake
   * sufficiency gate rejected them. Persists the observed metrics so the
   * admin UI can show exactly which threshold is the blocker and by how much.
   */
  z.object({
    status: z.literal("insufficient_data"),
    metrics: hazardDataMetricsSchema,
    thresholds: hazardDataMetricsSchema,
    lastCheckedAt: z.date(),
    nextCronRunAt: z.date(),
  }),
  z.object({
    status: z.literal("retry_scheduled"),
    nextAttemptAt: z.date(),
    failureCount: z.number(),
  }),
  z.object({ status: z.literal("failed"), failureCount: z.number() }),
]);
export type IntentStatus = z.infer<typeof intentStatusSchema>;

/**
 * Compute the admin-UI intent status from raw team settings. Pure function —
 * takes `now` as a parameter so it's trivially testable.
 */
export function computeIntentStatus({
  now,
  settings,
}: {
  now: Date;
  settings: PredictiveConversionSettings | undefined;
}): IntentStatus {
  const endpointSet = !!settings?.activeCheckoutHazardModel?.endpointName;
  const buildResult = settings?.checkoutHazardBuildResult;
  const intentRequested = settings?.intentRequested ?? false;
  const dataCollectionStartedAt = settings?.dataCollectionStartedAt;

  // Primary dimension: is the endpoint serving predictions?
  if (endpointSet) {
    if (buildResult?.status === "failed") {
      // Last retrain failed, but the previously-trained model is still
      // serving. We don't record the last *successful* build timestamp, so
      // we intentionally omit a "serving since" date for this variant.
      return {
        status: "live_retrain_failing",
        failureCount: buildResult.failureCount ?? 1,
        lastFailureAt: buildResult.completedAt,
      };
    }
    return {
      status: "live",
      // Only report `modelServingSince` when we have a verified successful
      // build timestamp. Falling back to `intentActivatedAt` or `now` would
      // be misleading (intentActivatedAt is "first ever activation," which
      // can be months before the currently-serving model was trained).
      modelServingSince:
        buildResult?.status === "succeeded"
          ? buildResult.completedAt
          : undefined,
    };
  }

  // Not serving. Forecast the earliest date this team could go live.
  const eligibleBaseline = dataCollectionStartedAt ?? now;
  const earliestEligibleToTrainAt = new Date(
    eligibleBaseline.getTime() + MIN_DATA_COLLECTION_DAYS * MS_PER_DAY,
  );
  const dataCollectionSatisfied =
    earliestEligibleToTrainAt.getTime() <= now.getTime();

  if (!intentRequested) {
    // If data collection is already satisfied, the bottleneck is the next
    // cron tick; otherwise it's the data-collection window.
    return {
      status: "not_requested",
      earliestLiveAt: dataCollectionSatisfied
        ? nextBuildReadinessCronRun(now)
        : earliestEligibleToTrainAt,
    };
  }

  // Intent is requested but endpoint not set.
  if (!dataCollectionSatisfied) {
    return {
      status: "collecting_data",
      eligibleToTrainAt: earliestEligibleToTrainAt,
    };
  }

  // Eligible to train. Check build result for retry state.
  if (buildResult?.status === "failed") {
    const failureCount = buildResult.failureCount ?? 1;
    if (failureCount >= MAX_INTENT_FAILURE_COUNT_BEFORE_FAILED) {
      return { status: "failed", failureCount };
    }
    const backoffDays = fibonacciDays(failureCount);
    const nextAttemptAt = new Date(
      buildResult.completedAt.getTime() + backoffDays * MS_PER_DAY,
    );
    return { status: "retry_scheduled", nextAttemptAt, failureCount };
  }

  // A build workflow is actively running (or timed out with the Step Function
  // still in progress). Show "training" instead of the misleading "queued."
  if (buildResult?.status === "running") {
    return {
      status: "training",
      startedAt: buildResult.completedAt,
      nextCronRunAt: nextBuildReadinessCronRun(now),
    };
  }

  // The workflow ran but the Snowflake sufficiency gate rejected this team.
  // Surface the specific blocker rather than lying about being "queued."
  // Missing `metrics` would indicate a pre-patch write; fall through to
  // queued_for_training in that case so the UI stays coherent.
  if (buildResult?.status === "insufficient_data" && buildResult.metrics) {
    return {
      status: "insufficient_data",
      metrics: buildResult.metrics,
      thresholds: HAZARD_DATA_SUFFICIENCY_THRESHOLDS,
      lastCheckedAt: buildResult.completedAt,
      nextCronRunAt: nextBuildReadinessCronRun(now),
    };
  }

  // Eligible, no failed/insufficient build on record, endpoint not set.
  // Waiting for the next cron run to kick off a build workflow.
  return {
    status: "queued_for_training",
    nextCronRunAt: nextBuildReadinessCronRun(now),
  };
}
